import { useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { authStore } from '../../stores/authStore';
import { getIndosDetails } from '../../api/authApi';
import accountCreatedIllustration from "../../assets/account-created.png";
import {TriangleAlert, CircleCheckBig, Eye, EyeOff} from 'lucide-react';
import { isValidEmail, normalizeEmail } from '../../utils/emailValidation';

const INDOS_INVALID_ERROR = 'INDOS number is wrong please enter correct INDOS number';
const INDOS_UNAVAILABLE_ERROR = 'Could not verify your INDoS number right now. Please try again.';
const EMAIL_INVALID_ERROR = 'Please enter a valid email address';

const SignupForm = () => {
  const snap = useSnapshot(authStore);
  const [currentStep, setCurrentStep] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Form data
  const [indianCdc, setIndianCdc] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [verifyingIndos, setVerifyingIndos] = useState(false);
  const [indosFieldError, setIndosFieldError] = useState(false);
  const [emailFieldError, setEmailFieldError] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(true);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [resending, setResending] = useState(false);

  const handleStepTransition = (nextStep) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep(nextStep);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 300);
  };

  // Clear error on input change
  const clearError = () => {
    if (snap.error) authStore.error = null;
  };

  // Clear error message whenever step changes
  useEffect(() => clearError(), [currentStep]);

  // Handle async step logic
  const handleSubmit = async (e) => {
    e?.preventDefault();
    clearError();
    let hasError = false;

    if (currentStep === 0) {
      if (!indianCdc.trim()) {
        authStore.error = 'Please enter your INDoS number';
        return;
      }
      if (!email.trim()) {
        authStore.error = 'Please enter your email';
        setEmailFieldError(true);
        return;
      }
      if (!isValidEmail(email)) {
        authStore.error = EMAIL_INVALID_ERROR;
        setEmailFieldError(true);
        return;
      }
      // Store email in lowercase so signup is case-insensitive
      setEmail(normalizeEmail(email));
      setVerifyingIndos(true);
      setIndosFieldError(false);
      setEmailFieldError(false);
      try {
        const response = await getIndosDetails(indianCdc.trim());
        if (response.success) {
          setVerifyingIndos(false);
          handleStepTransition(1);
        } else {
          authStore.error = INDOS_INVALID_ERROR;
          setIndosFieldError(true);
          setVerifyingIndos(false);
        }
      } catch {
        authStore.error = INDOS_UNAVAILABLE_ERROR;
        setVerifyingIndos(false);
      }
      return;
    }
    // Step 1: Password validation and API call
    else if (currentStep === 1) {
      // Validate password strength
      if (!Object.values(passwordStrength).every(Boolean)) {
        setPasswordError('Password must have at least 8 characters, letters, numbers, and special characters.');
        hasError = true;
      } else {
        setPasswordError('');
      }

      // Validate password match
      if (newPassword !== confirmPassword) {
        setConfirmPasswordError('Passwords do not match.');
        hasError = true;
      } else {
        setConfirmPasswordError('');
      }

      if (hasError) return;

      const res = await authStore.signup(
        indianCdc,
        normalizeEmail(email),
        newPassword,
        ""
      );
      if (!res) return;
      setVerificationEmailSent(res.verificationEmailSent !== false);
      setVerificationMessage(res.message || '');
      setResendError('');
      handleStepTransition(2);
    }
  };

  const checkPasswordStrength = (password) => {
    const hasLength = password.length >= 8;
    const hasNumbers = /\d/.test(password);
    const hasLetters = /[a-zA-Z]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    return { hasLength, hasNumbers, hasLetters, hasSpecial };
  };

  const handleResendVerification = async () => {
    setResendError('');
    setResending(true);
    try {
      const data = await authStore.resendVerificationEmail(normalizeEmail(email));
      setVerificationEmailSent(true);
      setVerificationMessage(data?.message || 'Verification email sent. Please check your inbox.');
    } catch (error) {
      setResendError(error?.message || 'Could not send the verification email.');
    } finally {
      setResending(false);
    }
  };

  const passwordStrength = checkPasswordStrength(newPassword);

  useEffect(() => {
    setPasswordError('');
    setConfirmPasswordError('');
  }, [newPassword, confirmPassword]);

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6 font-segoe">
            <div className="mt-6">
              <h2 className="font-helvetica text-3xl font-semibold mb-2">Create Account</h2>
              <p className="text-md">Please enter details correctly to make an account.</p>
            </div>
            {snap.error && (
              <div className="bg-red-500/80 flex items-center gap-2 backdrop-blur-sm text-white px-4 py-2 rounded-full text-xs font-light mb-6 w-fit opacity-75 animate-pulse transition-opacity duration-300 ease-in-out">
                <TriangleAlert />
                {snap.error}
              </div>
            )}
            <div>
              <label className="block text-sm font-light mb-2">INDoS NUMBER</label>
              <input
                type="text"
                className={`w-full px-4 py-3 glass lg:no-glass rounded-[1rem] border placeholder-white lg:placeholder-black/20 transition-all outline-none text-sm uppercase ${
                  indosFieldError || snap.error === INDOS_INVALID_ERROR
                    ? 'border-red-500 focus:border-red-500 ring-2 ring-red-500/50'
                    : 'border-black/50 focus:border-[var(--primary-font)]'
                }`}
                placeholder="Enter INDoS Number (e.g., XXAB1234)"
                value={indianCdc}
                onChange={e => {
                  setIndianCdc(e.target.value.toUpperCase());
                  clearError();
                  setIndosFieldError(false);
                }}
                maxLength={8}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-light mb-2">EMAIL ID</label>
              <input
                type="email"
                className={`w-full px-4 py-3 glass lg:no-glass rounded-[1rem] border placeholder-white lg:placeholder-black/20 transition-all outline-none text-sm ${
                  emailFieldError || snap.error === EMAIL_INVALID_ERROR
                    ? 'border-red-500 focus:border-red-500 ring-2 ring-red-500/50'
                    : 'border-black/55 focus:border-[var(--primary-font)]'
                }`}
                placeholder="your.email@gmail.com"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  clearError();
                  setEmailFieldError(false);
                }}
                required
              />
            </div>
            
            <button
              type="button"
              onClick={handleSubmit}
              className={`w-full py-3 rounded-[1rem] font-medium text-md bg-[var(--primary-font)] text-white hover:bg-[var(--primary-font)]/90 transition-colors mt-4 ${
                verifyingIndos || snap.loading ? 'opacity-60 cursor-not-allowed' : ''
              }`}
              disabled={verifyingIndos || snap.loading}
            >
              {verifyingIndos ? 'Verifying...' : snap.loading ? 'Processing...' : 'Continue'}
            </button>
            
            <div className="text-center text-sm">
              Already have an account? 
              <span 
                className="font-semibold hover:underline cursor-pointer transition-all ml-1"
                onClick={e => {
                  e.preventDefault();
                  if (typeof window !== 'undefined') 
                    window.dispatchEvent(new CustomEvent('switch-auth', { detail: { to: 'login' } }));
                }}
              >
                Login
              </span>
            </div>
          </div>
        );

      case 1:
        return (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="mt-4">
              <h2 className="font-helvetica text-3xl font-semibold mb-2">Create Password</h2>
              <p className="text-sm">Please create a strong password for maximum security.</p>
            </div>

            {snap.error && (
              <div className="bg-red-500/80 flex items-center gap-2 backdrop-blur-sm text-white px-4 py-2 rounded-full text-xs font-light mb-6 w-fit opacity-75 animate-pulse transition-opacity duration-300 ease-in-out">
                <TriangleAlert />
                {snap.error}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-light mb-2">NEW PASSWORD</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full px-4 py-3 pr-12 glass lg:no-glass rounded-[1rem] border border-black/55 placeholder-white lg:placeholder-black/20 focus:border-[var(--primary-font)] transition-all outline-none text-sm"
                  placeholder="Enter password..."
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); clearError(); if (passwordError && Object.values(checkPasswordStrength(e.target.value)).every(Boolean)) setPasswordError(''); }}
                  onBlur={e => {
                    if (!Object.values(checkPasswordStrength(e.target.value)).every(Boolean)) {
                      setPasswordError('Password must have at least 8 characters, letters, numbers, and special characters.');
                    }
                  }}
                  required
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-200 hover:text-gray-300 lg:text-gray-500 lg:hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {passwordError && (
                <div className="bg-red-500/80 flex items-center gap-2 backdrop-blur-sm text-white px-4 py-2 rounded-full text-xs font-light mb-6 w-fit opacity-75 animate-pulse transition-opacity duration-300 ease-in-out mt-2">
                  <TriangleAlert />
                  {passwordError}
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-light mb-2">CONFIRM PASSWORD</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="w-full px-4 py-3 pr-12 glass lg:no-glass rounded-[1rem] border border-black/15 placeholder-white lg:placeholder-black/20 focus:border-[var(--primary-font)] transition-all outline-none text-sm"
                  placeholder="Confirm password..."
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); clearError(); if (confirmPasswordError && e.target.value === newPassword) setConfirmPasswordError(''); }}
                  required
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-200 hover:text-gray-300 lg:text-gray-500 lg:hover:text-gray-700"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPasswordError && (
                <div className="bg-red-500/80 flex items-center gap-2 backdrop-blur-sm text-white px-4 py-2 rounded-full text-xs font-light mb-6 w-fit opacity-75 animate-pulse transition-opacity duration-300 ease-in-out mt-2">
                  <TriangleAlert />
                  {confirmPasswordError}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <p className="text-xs font-light text-white:text-[var(--primary-font)]">Your password must contain</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className={`flex items-center gap-2 ${passwordStrength.hasLength ? 'text-green-600' : 'text-gray-200/90 lg:text-gray-400'}`}>
                  <CircleCheckBig className={`${passwordStrength.hasLength ? 'text-green-600' : 'text-gray-200/90 lg:text-gray-400'} w-4 h-4`} />
                  8 or more characters
                </div>
                <div className={`flex items-center gap-2 ${passwordStrength.hasLetters ? 'text-green-600' : 'text-gray-200/90 lg:text-gray-400'}`}>
                  <CircleCheckBig className={`${passwordStrength.hasLetters ? 'text-green-600' : 'text-gray-200/90 lg:text-gray-400'} w-4 h-4`} />
                  Letters
                </div>
                <div className={`flex items-center gap-2 ${passwordStrength.hasNumbers ? 'text-green-600' : 'text-gray-200/90 lg:text-gray-400'}`}>
                  <CircleCheckBig className={`${passwordStrength.hasNumbers ? 'text-green-600' : 'text-gray-200/90 lg:text-gray-400'} w-4 h-4`} />
                  Numbers
                </div>
                <div className={`flex items-center gap-2 ${passwordStrength.hasSpecial ? 'text-green-600' : 'text-gray-200/90 lg:text-gray-400'}`}>
                  <CircleCheckBig className={`${passwordStrength.hasSpecial ? 'text-green-600' : 'text-gray-200/90 lg:text-gray-400'} w-4 h-4`} />
                  Special Characters
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={() => handleStepTransition(0)}
                className="flex-1 py-3 rounded-[1rem] font-medium text-md border border-[var(--primary-font)] text-white lg:text-[var(--primary-font)] hover:bg-[var(--primary-font)]/10 transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={!(Object.values(passwordStrength).every(Boolean) && newPassword === confirmPassword) || snap.loading}
                className={
                  `flex-1 py-3 rounded-[1rem] font-medium text-md bg-[var(--primary-font)] text-white hover:bg-[var(--primary-font)]/90 transition-colors ` +
                  (!(Object.values(passwordStrength).every(Boolean) && newPassword === confirmPassword) || snap.loading
                    ? 'opacity-50 pointer-events-none'
                    : '')
                }
              >
                {snap.loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </form>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="mt-6 text-left">
              <h2 className="font-helvetica text-3xl font-bold mb-2">
                {verificationEmailSent ? "Check Your Mail!" : "Account Created"}
              </h2>
              <p className="text-sm">
                {verificationEmailSent
                  ? "We just sent you a verification link."
                  : verificationMessage || "Your account was created, but we could not send the verification email."}
              </p>
              {resendError && (
                <p className="text-sm text-red-600 mt-2">{resendError}</p>
              )}
            </div>            

            <img src={accountCreatedIllustration} alt="Verification Pending" className="w-full h-auto" />

            {!verificationEmailSent && (
              <button
                type="button"
                disabled={resending}
                className={`w-full py-3 rounded-[1rem] font-medium text-md border border-[var(--primary-font)] text-white lg:text-[var(--primary-font)] hover:bg-[var(--primary-font)]/10 transition-colors ${resending ? 'opacity-50 pointer-events-none' : ''}`}
                onClick={handleResendVerification}
              >
                {resending ? 'Sending...' : 'Resend verification email'}
              </button>
            )}
            
            <button
              type="button"
              className="w-full py-3 rounded-[1rem] font-medium text-md bg-[var(--primary-font)] text-white hover:bg-[var(--primary-font)]/90 transition-colors"
              onClick={e => {
                e.preventDefault();
                if (typeof window !== 'undefined') 
                  window.dispatchEvent(new CustomEvent('switch-auth', { detail: { to: 'login' } }));
              }}
            >
              {"Back to Login"}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div 
      className={`transition-opacity duration-300 text-white lg:text-[var(--primary-font)] font-segoe ${
        isTransitioning ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {renderStep()}
    </div>
  );
};

export default SignupForm;